import React from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { apiPost } from '@/lib/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

const schema = z.object({
  email: z.string().min(1, 'E-posta zorunludur').email('Geçerli bir e-posta girin'),
})
type FormData = z.infer<typeof schema>

export const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = React.useState(false)
  const [serverError, setServerError] = React.useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setServerError('')
    try {
      await apiPost('/auth/forgot-password', { email: data.email })
      setSent(true)
    } catch {
      setServerError('Bir hata oluştu. Lütfen tekrar deneyin.')
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
            <Mail className="h-6 w-6 text-white" />
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-card">
          {sent ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-400" />
              </div>
              <h1 className="text-xl font-bold text-slate-100 mb-2">E-posta Gönderildi</h1>
              <p className="text-sm text-slate-400 mb-6">
                Şifre sıfırlama talimatları e-posta adresinize gönderildi.
                Gelen kutunuzu ve spam klasörünüzü kontrol edin.
              </p>
              <Link
                to="/giris"
                className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Girişe Dön
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-slate-100">Şifremi Unuttum</h1>
                <p className="text-sm text-slate-400 mt-1">
                  E-posta adresinizi girin, sıfırlama token'ını göndereceğiz.
                </p>
              </div>

              {serverError && (
                <div role="alert" className="mb-4 rounded-lg bg-red-900/20 border border-red-800/50 p-3 text-sm text-red-400">
                  {serverError}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                <Input
                  label="E-posta Adresi"
                  type="email"
                  placeholder="ornek@okul.com"
                  autoComplete="email"
                  autoFocus
                  error={errors.email?.message}
                  {...register('email')}
                />

                <Button type="submit" fullWidth loading={isSubmitting}>
                  {isSubmitting ? 'Gönderiliyor…' : 'Sıfırlama Linki Gönder'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/giris"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Girişe Dön
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
